import { createStore } from 'vuex';
import {
  exchangeAccessCode,
  getUserData,
  getProjectModels,
  goToSpeckleAuthPage,
  speckleLogOut, getProjectObject, getProjectModelId, getProjectModelViewerResource, getAllProjects,
  getProjectBracingWalls
} from "@/speckleUtils"
import { getProjectChildrenSample } from "@/speckleUtils"
import { moduleViewer } from "@/store/modules/moduleViewer";
import { getUserQuery, getProjectByIdQuery } from "@/qwebApiQueries";
import { createProjectMutation, updatePanelCountEstimatorMutation, updatePanelEstimationMutation, updateCarbonImpactMutation, updateBracingResultsMutation, updateProjectInfoMutation } from "@/qwebApiMutations";
import {qwebApi} from "@/store/modules/qwebApi";

const defaultProjectInfo = {
  name: null,
  number: null,
  description: null,
  clientName: null,
  constructionStartDate: null,
  location: {
    postalCode: null,
    street: null,
    number: null,
    city: null,
    province: null,
    country: null,
  },
};

const store = createStore({
  state: {
    userAgent: null,
    user: null,
    serverInfo: null,
    currentModel: null,
    customer: {
      selectedWallType: [],
      totalArea: 0,
      totalCurveLength: 0,
    },
    latestModels: null,
    previousCursors: [null],
    tableOptions: null,
    scheduleTable: {
      selectedWallType: null,
      headers: [],
      values: [],
      walls: {},
    },
    projectInfo: {
      ...defaultProjectInfo,
    },
    maxPanelThickness: 12,
    isModelCollapsed: false,
    projectData: {},
    bracingWalls: []
  },
  getters: {
    userAgent: state => state.userAgent ?? 'desktop',
    isAuthenticated: state => state.user != null,
  },
  mutations: {
    setUserAgent(state, userAgent) {
      state.userAgent = userAgent;
    },
    setUser(state, user) {
      state.user = user;
    },
    setServerInfo(state, info) {
      state.serverInfo = info;
    },
    setCurrentProject(state, project) {
      state.projectData.projectId = project?.id ?? null;
      state.projectInfo.name = project?.name ?? null;
    },
    setCurrentModel(state, model) {
      state.currentModel = model;
    },
    setModels(state, models) {
      state.latestModels = models;
    },
    setCustomerSelectedWallType(state, walls) {
      let wallsData = [];

      walls.forEach((wall) => {
        const totalHostAreaComputed = wall.parameters.reduce((total, param) => {
          return total + (param.area || 0);
        }, 0);

        const totalCurveElemLength = wall.parameters.reduce((total, param) => {
          return total + (param.curveLength || 0);
        }, 0);

        wall.width = wall.parameters.find(param => param.width)?.width || 0;
        wall.area = totalHostAreaComputed;
        wall.curveLength = totalCurveElemLength;
        wallsData.push(wall);
      });
      state.customer.selectedWallType = wallsData;

      let totalArea = 0;
      let totalCurveLength = 0;
      state.customer.selectedWallType.forEach((wall) => {
        totalArea += wall.area;
        totalCurveLength += wall.curveLength
      });
      state.customer.totalArea = totalArea;
      state.customer.totalCurveLength = totalCurveLength;
    },
    setTableOptions(state, options) {
      state.tableOptions = options;
    },
    setScheduleTable(state, data) {
      state.scheduleTable.headers = [];
      state.scheduleTable.values = [];
      state.scheduleTable.walls = {};

      const headers = data[0];
      const values = data.slice(1);

      // align: index > 0 ? 'end' : 'start'
      headers.forEach((title) => {
        let key = title.toLowerCase()
          .replace(/[()]/g, "")
          .replaceAll(/[ /]/g, "_");
        state.scheduleTable.headers.push({
          title: title,
          key: key,
          align: 'center',
          sortable: false,
        });
      });

      values.forEach((row) => {
        let formattedRow = {};
        row.forEach((value, index) => {
          formattedRow[state.scheduleTable.headers[index].key] = value;
        });
        state.scheduleTable.values.push(formattedRow);
      });

      state.scheduleTable.values.forEach((value) => {
        // eslint-disable-next-line no-prototype-builtins
        if (!state.scheduleTable.walls.hasOwnProperty(value.wall_type)) {
          state.scheduleTable.walls[value.wall_type] = [];
        }
        state.scheduleTable.walls[value.wall_type].push(value);
      });
    },
    setScheduleSelectedWallType(state, wallType) {
      state.scheduleTable.selectedWallType = wallType;
    },
    setMaxPannelThickness(state, thickness) {
      state.maxPanelThickness = thickness;
    },
    setIsModelCollapsed(state, isModelColapsed) {
      state.isModelCollapsed = isModelColapsed;
    },
    setProjectLanguage(state, lang) {
      state.projectData.projectLang = lang;
    },
    setProjectInfo(state, payload) {
      state.projectInfo = {
        ...defaultProjectInfo,
        ...payload.info,
        location: {
          ...defaultProjectInfo.location,
          ...(payload.info?.location ?? {}),
        },
      };
    },
    resetPrevCursors(state) {
      state.previousCursors = [null];
    },
    addCursorToPreviousList(state, cursor) {
      state.previousCursors.push(cursor);
    },
    setProjectData(state, projectData) {
      state.projectData = projectData;
    },
    setPanelCountEstimatorData(state, panelCountData) {
      if (state.projectData) {
        state.projectData.panelCountEstimator = panelCountData;
      }
    },
    setPanelEstimationData(state, panelEstimationData) {
      if (state.projectData && state.projectData.panelCountEstimator) {
        state.projectData.panelCountEstimator.panelEstimation = panelEstimationData;
      }
    },
    setCarbonImpactData(state, carbonImpactData) {
      if (state.projectData) {
        state.projectData.carbonImpactEstimator = carbonImpactData;
      }
    },
    setBracingWalls(state, walls) {
      state.bracingWalls = walls;
    },
    setBracingResultsData(state, bracingResultsData) {
      if (state.projectData) {
        state.projectData.bracingResults = bracingResultsData;
      }
    }
  },
  actions: {
    logout(context) {
      // Wipe the state
      context.commit("setUser", null);
      context.commit("setServerInfo", null);
      context.commit("setCurrentProject", null);
      context.commit("setCurrentModel", null);
      context.commit("setModels", null);
      context.commit("setTableOptions", null);
      context.commit("resetPrevCursors");
      context.commit("setProjectLanguage", null);
      context.commit("setProjectData", {});
      context.commit("setProjectInfo", { info: { ...defaultProjectInfo } });
      context.commit("setCustomerSelectedWallType", []);
      context.commit("setScheduleTable", [[]]);
      context.commit("setScheduleSelectedWallType", null);
      context.commit("setMaxPannelThickness", 12);
      context.commit("setIsModelCollapsed", false);
      // Clear localStorage
      localStorage.removeItem("projectId");
      localStorage.removeItem("modelId");
      // Wipe the tokens
      speckleLogOut();
    },
    exchangeAccessCode(context, accessCode) {
      // Here, we could save the tokens to the store if necessary.
      return exchangeAccessCode(accessCode);
    },
    async getUser(context) {
      try {
        var json = await getUserData();
        var data = json.data;
        context.commit("setServerInfo", data.serverInfo);
        try {
          const apiUrl = process.env.VUE_APP_QWEBAPI_URL;
          if (apiUrl) {
            const query = getUserQuery(data.activeUser.email);
            const response = await fetch(apiUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify({ query })
            });
            const result = await response.json();
            const userRoleResponse = result.data?.users?.[0];
            const manufacturerId = userRoleResponse?.linkId ?? data.activeUser?.manufacturerId ?? data.activeUser?.linkId ?? null;
            context.commit("setUser", {
              ...data.activeUser,
              role: userRoleResponse?.role ?? data.activeUser.role,
              linkId: userRoleResponse?.linkId ?? data.activeUser.linkId ?? null,
              manufacturerId
            });
          } else {
            context.commit("setUser", data.activeUser);
          }
        } catch (apiErr) {
          console.error("Failed to fetch user role:", apiErr);
          context.commit("setUser", data.activeUser);
        }
      } catch (err) {
        console.error(err);
      }
    },
    redirectToAuth() {
      goToSpeckleAuthPage();
    },
    async handleProjectSelection(context, project) {

      if (project.name == null) {
        try {
          const json = await getAllProjects();
          const projects = json.data.activeUser.projects.items;
          project.name = projects.find(p => p.id === project.id)?.name ?? null;
        } catch (err) {
          console.warn('Failed to fetch project name:', err);
          project.name = null;
        }
      }
      
      context.commit("setCurrentProject", project);
      context.commit("setTableOptions", { itemsPerPage: 5 });
      context.commit("resetPrevCursors");
      var json = await getProjectModels(project.id, 5, null);
      context.commit("setModels", json.data.project.models);
    },
    async handleModelSelection(context, modelId) {
      if(modelId && context.state.latestModels) {
        var model = context.state.latestModels.items.find(m => m.id === modelId);
        context.commit("setCurrentModel", model);
        try {
          const projectId = context.state.projectData?.projectId ?? null;
          if (!projectId) {
            context.commit("setProjectLanguage", null);
            return;
          }
          const vrResp = await getProjectModelViewerResource(projectId, model.id);
          const resourceId = vrResp?.data?.project?.viewerResources?.[0]?.items?.[0]?.objectId;
          if (!resourceId) {
            context.commit("setProjectLanguage", null);
            return;
          }
          const sampleResp = await getProjectChildrenSample(projectId, resourceId, 1);
          const sampleObj = sampleResp?.data?.project?.object?.children?.objects?.[0]?.data;
          const category = sampleObj?.category ?? sampleObj?.Category ?? null;
          let lang = null;
          if (typeof category === "string") {
            const categoryLowerCase = category.toLowerCase();
            if (categoryLowerCase.includes("murs")) lang = "fr";
            else if (categoryLowerCase.includes("walls")) lang = "en";
            else lang = "unknown";
          } else {
            lang = "unknown";
          }
          context.commit("setProjectLanguage", lang);
        } catch (e) {
          console.error("Failed to detect model language:", e);
          context.commit("setProjectLanguage", null);
        }
      }
    },
    async getModels(context, cursor) {
      var json = await getProjectModels(
        context.state.projectData?.projectId ?? null,
        5,
        cursor
      );
      context.commit("setModels", json.data.project.models);
    },
    clearProjectSelection(context) {
      context.commit("setCurrentProject", null);
      context.commit("setCurrentModel", null);
      context.commit("setModels", null);
      context.commit("setTableOptions", null);
      context.commit("resetPrevCursors", [null]);
    },
    async fetchScheduleTableData(context) {
      let tableData = [];
      //TODOEB: this should not be hardcoded, at least a config
      const projectId = "144ada10be";
      // Get modelId for schedule
      let response = await getProjectModelId(projectId, "schedule");
      const modelId = response.data.project.modelByName.id;
      // Get Speckle viewer resource id
      response = await getProjectModelViewerResource(projectId, modelId);
      const resourceId = response.data.project.viewerResources[0].items[0].objectId;
      // Fetch schedule data
      response = await getProjectObject(projectId, resourceId);
      try {
        const schedulesId = response.data.project.object.data["@Schedules"][0].referencedId;
        response = await getProjectObject(projectId, schedulesId);
        tableData = response.data.project.object.data.data;

        if (!Array.isArray(tableData)) {
          console.warn("The Schedule data table is empty");
          tableData = [];
        }
      } catch (e) {
        console.error(`Failed to fetch Schedules data on the Speckle server: ${e}`)
      }
      context.commit("setScheduleTable", tableData);
    },
    handleMaxPanelThicknessChange(context, thickness) {
      context.commit("setMaxPannelThickness", thickness);
    },
    handleIsModelCollapsed(context, isModelCollapsed) {
      context.commit("setIsModelCollapsed", isModelCollapsed);
    },
    fetchProjectInfo({ state, commit }) {
      const dbProjectInfo = state.projectData?.projectInfo ?? {};
      commit("setProjectInfo", {
        info: {
          ...defaultProjectInfo,
          ...dbProjectInfo,
          location: {
            ...defaultProjectInfo.location,
            ...(dbProjectInfo?.location ?? {}),
          },
          // Keep the selected Speckle project name as fallback when DB has no value.
          name: dbProjectInfo?.name ?? state.projectInfo?.name ?? null,
        },
      });
    },
    async getOrCreateProject(context) {
      try {
        const projectId = context.state.projectData?.projectId ?? null;
        const projectLang = context.state.projectData?.projectLang ?? null;

        if (!projectId) {
          console.error("Project ID not found");
          return;
        }

        const apiUrl = process.env.VUE_APP_QWEBAPI_URL;
        if (!apiUrl) {
          console.warn("QwebAPI URL not configured");
          return;
        }

        // Try to fetch existing project
        const query = getProjectByIdQuery();
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            query,
            variables: { projectId }
          })
        });

        const result = await response.json();
        let projectData = result.data?.project;

        // If project doesn't exist, create it
        if (!projectData) {
          const mutation = createProjectMutation();
          const createResponse = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              query: mutation,
              variables: {
                input: {
                  projectId,
                  projectLang,
                  projectInfo: {
                    ...defaultProjectInfo,
                    name: context.state.projectInfo?.name ?? null,
                    location: {
                      ...defaultProjectInfo.location,
                    },
                  },
                  panelCountEstimator: {
                    panelConfiguration: {
                      maxPanelWidth: null,
                      maxPanelLength: null
                    },
                    selectedWalls: [],
                    prefabricatedWallType: null,
                    panelEstimation: {
                      completePanels: null,
                      irregularPanels: null,
                      numberOfTrucks: null,
                      truckDimensions: {
                        length: null,
                        width: null,
                        height: null
                      },
                      spacerThickness: null
                    }
                  },
                  carbonImpactEstimator: {
                    materialtotals: {
                      area: null,
                      volume: null,
                      mass: null,
                      carbonFootprint: null
                    }
                  }
                }
              }
            })
          });

          const createResult = await createResponse.json();
          projectData = createResult.data?.createProject;
        }

        if (projectData) {
          context.commit("setProjectData", projectData);
          context.commit("setProjectInfo", {
            info: {
              ...defaultProjectInfo,
              ...projectData.projectInfo,
              location: {
                ...defaultProjectInfo.location,
                ...(projectData.projectInfo?.location ?? {}),
              },
              name: projectData.projectInfo?.name ?? context.state.projectInfo?.name ?? null,
            },
          });
        }
      } catch (error) {
        console.error("Error in getOrCreateProject:", error);
      }
    },
    async saveProjectInfo(context, payload) {
      const normalizedPayload = {
        ...defaultProjectInfo,
        ...payload,
        location: {
          ...defaultProjectInfo.location,
          ...(payload?.location ?? {}),
        },
      };

      try {
        const projectId = context.state.projectData?.projectId ?? null;
        if (!projectId) {
          console.error("Project ID not found");
          return;
        }

        const apiUrl = process.env.VUE_APP_QWEBAPI_URL;
        if (!apiUrl) {
          console.warn("QwebAPI URL not configured");
          context.commit("setProjectInfo", { info: normalizedPayload });
          return;
        }

        const mutation = updateProjectInfoMutation();
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            query: mutation,
            variables: {
              projectId,
              input: normalizedPayload
            }
          })
        });

        const result = await response.json();

        if (result.data?.updateProjectInfo) {
          context.commit("setProjectData", result.data.updateProjectInfo);
          context.commit("setProjectInfo", {
            info: {
              ...defaultProjectInfo,
              ...result.data.updateProjectInfo.projectInfo,
              location: {
                ...defaultProjectInfo.location,
                ...(result.data.updateProjectInfo.projectInfo?.location ?? {}),
              },
            },
          });
        }
      } catch (error) {
        console.error("Error in saveProjectInfo:", error);
      }
    },
    async savePanelCountEstimatorData(context, payload) {
      try {
        const projectId = context.state.projectData?.projectId;
        if (!projectId) {
          console.error("Project ID not found");
          return;
        }

        const apiUrl = process.env.VUE_APP_QWEBAPI_URL;
        if (!apiUrl) {
          console.warn("QwebAPI URL not configured");
          context.commit("setPanelCountEstimatorData", payload);
          return;
        }

        const mutation = updatePanelCountEstimatorMutation();
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            query: mutation,
            variables: {
              projectId,
              input: payload
            }
          })
        });

        const result = await response.json();

        if (result.data?.updatePanelCountEstimator) {
          context.commit("setProjectData", result.data.updatePanelCountEstimator);
        }
      } catch (error) {
        console.error("Error in savePanelCountEstimatorData:", error);
      }
    },
    async savePanelEstimationData(context, payload) {
      try {
        const projectId = context.state.projectData?.projectId ?? null;
        if (!projectId) {
          console.error("Project ID not found");
          return;
        }

        const apiUrl = process.env.VUE_APP_QWEBAPI_URL;
        if (!apiUrl) {
          console.warn("QwebAPI URL not configured");
          context.commit("setPanelEstimationData", payload);
          return;
        }

        const mutation = updatePanelEstimationMutation();
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            query: mutation,
            variables: {
              projectId,
              input: payload
            }
          })
        });

        const result = await response.json();

        if (result.data?.updatePanelEstimation) {
          context.commit("setProjectData", result.data.updatePanelEstimation);
        }
      } catch (error) {
        console.error("Error in savePanelEstimationData:", error);
      }
    },
    async saveCarbonImpactData(context, payload) {
      try {
        const projectId = context.state.projectData?.projectId ?? null;
        if (!projectId) {
          console.error("Project ID not found");
          return;
        }

        const apiUrl = process.env.VUE_APP_QWEBAPI_URL;
        if (!apiUrl) {
          console.warn("QwebAPI URL not configured");
          context.commit("setCarbonImpactData", payload);
          return;
        }

        const mutation = updateCarbonImpactMutation();
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            query: mutation,
            variables: {
              projectId,
              input: payload
            }
          })
        });

        const result = await response.json();
        if (result.data?.updateCarbonImpact) {
          context.commit("setProjectData", result.data.updateCarbonImpact);
        }
      } catch (error) {
        console.error("Error in saveCarbonImpactData:", error);
      }
    },
    async saveBracingResults(context, payload) {
      try {
        const projectId = context.state.projectData?.projectId ?? null;
        if (!projectId) {
          console.error("Project ID not found");
          return;
        }

        const apiUrl = process.env.VUE_APP_QWEBAPI_URL;
        if (!apiUrl) {
          console.warn("QwebAPI URL not configured");
          context.commit("setBracingResultsData", payload);
          return;
        }

        const mutation = updateBracingResultsMutation();
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            query: mutation,
            variables: {
              projectId,
              input: payload
            }
          })
        });

        const result = await response.json();
        if (result.data?.updateBracing) {
          context.commit("setProjectData", result.data.updateBracing);
        }
      } catch (error) {
        console.error("Error in saveBracingResults:", error);
      }
    },
    async getBracingWalls(context) {
      function getNested(obj, path) {
        return path.split('.').reduce((o, key) => (o ? o[key] : undefined), obj);
      }
      try {
        const projectId = context.state.projectData?.projectId;
        const modelId = context.state.currentModel?.versions.items[0].referencedObject;
        const language = context.state.projectData?.projectLang || 'en';
        if (!projectId || !modelId) {
          console.error("Project ID or Model ID not found");
          return [];
        }
        const response = await getProjectBracingWalls(projectId, modelId, language);
        const walls = response.data.project.object.children.objects.map(obj => {
          const data = obj.data;

          const orientationPath = language === 'fr'
            ? "properties.Parameters.Type Parameters.Données d'identification.Nom du type.value"
            : "properties.Parameters.Type Parameters.Identity Data.Type Name.value";
          const lengthPath = language === 'fr'
            ? "properties.Parameters.Instance Parameters.Cotes.Length.value"
            : "properties.Parameters.Instance Parameters.Dimensions.Length.value";
          const validatedPath = language === 'fr'
            ? "properties.Parameters.Instance Parameters.Autre.Validation.value"
            : "properties.Parameters.Instance Parameters.Other.Validation.value";
          const shearNumberPath = language === 'fr'
            ? "properties.Parameters.Instance Parameters.Autre.Shear Number.value"
            : "properties.Parameters.Instance Parameters.Other.Shear Number.value";

          return {
            id: data.id,
            name: data.name,
            elementId: getNested(data, "properties.elementId"),
            orientation: getNested(data, orientationPath)?.[0],
            length: getNested(data, lengthPath),
            shearNumber: getNested(data, shearNumberPath) ?? null,
            type: null,
            validated: getNested(data, validatedPath) === 'Yes',
          };
        });

        // Group walls by shearNumber so that walls sharing the same Shear Number
        // are represented as a single entry in the list.
        // Walls without a Shear Number each form their own single-wall group, using
        // their individual Speckle id as the key to avoid unintended merging.
        const groupMap = new Map();
        for (const wall of walls) {
          const key = wall.shearNumber ?? wall.id;
          if (!groupMap.has(key)) {
            groupMap.set(key, {
              shearNumber: wall.shearNumber,
              ids: [],
              elementIds: [],
              name: wall.name,
              orientation: wall.orientation,
              length: wall.length,
              type: null,
              validated: wall.validated,
            });
          }
          const group = groupMap.get(key);
          group.ids.push(wall.id);
          group.elementIds.push(wall.elementId);
        }

        const groupedWalls = Array.from(groupMap.values());

        // Walls sharing the same Shear Number necessarily share the same properties
        // (orientation, length, type), so we restore the type from any matching saved wall.
        const savedValidatedWalls = context.state.projectData?.bracing?.validatedWalls ?? [];
        return groupedWalls.map(group => {
          const savedWall = savedValidatedWalls.find(v => group.ids.includes(v._id));
          return {
            ...group,
            type: savedWall?.type ?? group.type,
          };
        });
      } catch (error) {
        console.error("Error fetching bracing walls:", error);
        return [];
      }
    }
  },
  modules: {
    modelViewer: moduleViewer,
    qwebApi: qwebApi
  }
});

export default store;